// src/components/SubscriptionFunnel/BrandedSignup.js
import React, { useState } from 'react';
import { User, Mail, Phone, MessageSquareText, CheckCircle } from 'lucide-react';
import SpeechToTextRecorder from '../tools/SpeechToTextRecorder';
import ThreeSphere from '../AMyteSphere/ThreeSphere';

const BrandedSignup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');
  const [message, setMessage] = useState('');

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const normalizePhone = (input) => {
    const digits = input.replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
    if (digits.startsWith('+') && digits.length >= 11) return digits;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !emailConfirm || !phone) {
      setMessage('Please fill in all required fields.');
      return;
    }

    if (!isValidEmail(email)) {
      setMessage('Invalid email format.');
      return;
    }

    if (email.trim() !== emailConfirm.trim()) {
      setMessage('Email addresses do not match.');
      return;
    }

    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) {
      setMessage('Invalid phone number. Please use digits only (10–15 digits).');
      return;
    }

    try {
      const payload = {
        name,
        email,
        phone: normalizedPhone,
        comment,
      };

      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        setMessage('Subscription successful!');
      } else {
        setMessage(data.error || 'Something went wrong.');
      }
    } catch (err) {
      console.error(err);
      setMessage('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen relative bg-black flex items-center justify-center px-4 text-white overflow-hidden">
      {/* Background Sphere */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ThreeSphere zoomMultiplier={3.2} />
      </div>

      {/* Signup Card */}
      <div className="relative z-10 w-full max-w-md bg-white/90 backdrop-blur-md text-black rounded-xl shadow-lg p-8 sm:p-10">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/white_logo_transparent.png"
            alt="Myte Logo"
            className="h-12 object-contain"
          />
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#1F5683]">Join the AI Revolution</h1>
          <p className="text-sm text-gray-700 mt-1">Get your training asset now - begin building your path.</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="mb-5 relative">
            <label className="block mb-1 font-semibold">Name</label>
            <User className="absolute left-3 top-11 text-gray-400 h-4 w-4" />
            <input
              className="w-full p-3 pl-10 border border-gray-300 rounded-md"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          {/* Email */}
          <div className="mb-5 relative">
            <label className="block mb-1 font-semibold">Email</label>
            <Mail className="absolute left-3 top-11 text-gray-400 h-4 w-4" />
            <input
              className="w-full p-3 pl-10 border border-gray-300 rounded-md"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Confirm Email */}
          <div className="mb-5 relative">
            <label className="block mb-1 font-semibold">Confirm Email</label>
            <Mail className="absolute left-3 top-11 text-gray-400 h-4 w-4" />
            <input
              className="w-full p-3 pl-10 border border-gray-300 rounded-md"
              type="email"
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              required
            />
          </div>

          {/* Phone */}
          <div className="mb-5 relative">
            <label className="block mb-1 font-semibold">Phone</label>
            <Phone className="absolute left-3 top-11 text-gray-400 h-4 w-4" />
            <input
              className="w-full p-3 pl-10 border border-gray-300 rounded-md"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>

          {/* Comment via Speech to Text */}
          <div className="mb-5">
            <SpeechToTextRecorder
              label={
                <span className="flex items-center gap-2 font-semibold">
                  <MessageSquareText className="h-4 w-4 text-gray-600" />
                  Speak or Type Additional Notes
                </span>
              }
              placeholder="Give me details about your needs... I can build it or prepare a class for it for free."
              value={comment}
              onChange={(txt) => setComment(txt)}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="mt-4 w-full py-3 text-lg font-bold
                       bg-gradient-to-r from-[#1F5683] to-[#246399]
                       text-white rounded-md flex items-center justify-center gap-2 hover:scale-[1.01] transition-transform"
          >
            <CheckCircle className="h-5 w-5" />
            Submit
          </button>
        </form>

        {message && (
          <p className="mt-4 text-center font-semibold text-red-600">
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default BrandedSignup;
